import type {
  Agent,
  AgentBody,
  AgentFile,
  InternalToolCatalogEntry,
  OutboundCallBody,
  OutboundCallResponse,
} from "@/schemas/agent.schema";
import type { ApiResponse, RequestConfig } from "@/services/api";
import { z } from "zod";
import { InternalToolCatalogEntrySchema } from "@/schemas/agent.schema";
import { api } from "@/services/api";
import { createLoopbackCrud } from "@/services/crud";

export type ImportAgent = Pick<
  Agent,
  "description" | "instructions" | "parameters" | "tools" | "llm_model" | "name"
> & {
  creator: {
    full_name: string;
    email: string;
    organization: string;
  };
};

export const agentsApi = {
  ...createLoopbackCrud<Agent, AgentBody>("/agents"),

  async get(config?: RequestConfig) {
    const { data } = (await api("/agents", config)) as ApiResponse<Agent[]>;
    return data;
  },

  /**
   * Fetch the server internal-tools catalog (source of truth for valid
   * internal tool names, descriptions and deprecation flags).
   */
  async listInternalTools(): Promise<InternalToolCatalogEntry[]> {
    const { data } = await api.get<unknown>("/agents/internal-tools");
    return z.array(InternalToolCatalogEntrySchema).parse(data);
  },

  async deleteById(id: string, config?: RequestConfig) {
    await api.delete(`/agents/${id}`, config);
  },

  async uploadFile({
    agent_id,
    file,
    chunk_size,
    chunk_overlap,
    separators,
    model_name,
  }: {
    agent_id: string;
    file: File;
    chunk_size?: number;
    chunk_overlap?: number;
    separators?: string;
    model_name?: string;
  }): Promise<AgentFile> {
    const formData = new FormData();
    formData.append("file", file, file.name);

    const { data } = await api.post(`/agent-files/${agent_id}/upload-file`, formData, {
      params: {
        chunk_size,
        chunk_overlap,
        separators,
        model_name,
      },
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return data;
  },

  async estimateFileCost(
    file: File,
    model: string
  ): Promise<{
    cost: number;
    input_tokens: number;
  }> {
    const formData = new FormData();
    formData.append("file", file, file.name);

    const { data } = await api.post(`/agent-files/estimate-cost`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: {
        model_name: model,
      },
    });

    return data;
  },

  async deleteFile(collection_name: string) {
    await api.delete(`/agent-files/${collection_name}`);
  },

  async importById(id: string) {
    const { data } = await api.get(`/agents/${id}/import`);

    return data as ImportAgent;
  },

  async getLivekitToken(
    agentId: string,
    sessionOverrides?: {
      additional_instructions?: string;
      variables?: Record<string, string>;
      thread_id?: string;
      // Session TTS pick (composer voice picker): the server resolves the
      // worker routing and strips the raw keys when unroutable.
      tts_model?: string;
      tts_voice?: string;
      // Session LLM pick (composer model trigger): same fail-open contract —
      // the voice session is built from the agent otherwise.
      llm_model?: string;
    }
  ) {
    const overrides =
      sessionOverrides &&
      (sessionOverrides.additional_instructions || sessionOverrides.variables || sessionOverrides.thread_id || sessionOverrides.tts_model || sessionOverrides.tts_voice || sessionOverrides.llm_model)
        ? sessionOverrides
        : {};

    const { data } = await api.post<{
      token: string;
      url: string;
      room: string;
    }>(`/agents/${agentId}/livekit-token`, {
      ...overrides,
      // AI-999: este token é consumido imediatamente pelo connect (único
      // caller é o fluxo de chamada de voz) — opt-in pro server pré-criar a
      // sala e despachar o agente em paralelo com o connect do browser.
      // Servers antigos ignoram o campo.
      pre_connect: true,
    });

    return data;
  },

  // Fazer chamada outbound
  makeOutboundCall: async (agentId: string, body: OutboundCallBody) => {
    const { data } = await api.post<OutboundCallResponse>(`/agents/${agentId}/outbound-call`, body);
    return data;
  },
};
